import Layout from '../../components/Layout';
import ProductCard from '../../components/ProductCard';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const products = [
  {
    name: "Gentle Salicylic Acid Cleanser",
    description: "A low-concentration BHA cleanser that helps clear pores without over-drying.",
    bestFor: "Acne-Prone Skin",
    benefits: ["2% Salicylic acid", "Clears pore buildup", "Non-drying formula"]
  },
  {
    name: "Oil-Free Gel Moisturizer",
    description: "Lightweight hydration that won't clog pores or trigger breakouts.",
    bestFor: "Oily & Acne-Prone",
    benefits: ["Oil-free formula", "Non-comedogenic", "Soothing ingredients"]
  },
  {
    name: "Niacinamide Serum (10%)",
    description: "Helps regulate oil production and minimize the appearance of pores.",
    bestFor: "Oily & Acne-Prone",
    benefits: ["Controls sebum", "Minimizes pores", "Reduces redness"]
  },
  {
    name: "Benzoyl Peroxide Spot Treatment",
    description: "Targeted treatment for active breakouts. Use sparingly.",
    bestFor: "Active Breakouts",
    benefits: ["Kills acne bacteria", "Fast-acting", "Spot treatment format"]
  },
  {
    name: "Hydrocolloid Pimple Patches",
    description: "Absorb fluid from whiteheads and protect healing skin.",
    bestFor: "Whiteheads",
    benefits: ["Absorbs fluid", "Protects from picking", "Invisible wear"]
  },
  {
    name: "Lightweight Mineral SPF 30",
    description: "Gentle, non-comedogenic sun protection for acne-prone skin.",
    bestFor: "Acne-Prone Skin",
    benefits: ["Mineral formula", "Non-comedogenic", "No white cast"]
  }
];

const Acne = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="acne-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Skin Concern
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Acne-Prone Skin Guide
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Managing acne requires patience and the right products. This guide covers gentle, effective options that won't irritate your skin while helping to clear and prevent breakouts.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Understanding Acne
          </h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            Acne occurs when pores become clogged with oil, dead skin cells, and bacteria. Factors like hormones, stress, diet, and skincare products can all play a role.
          </p>
          <p className="text-[#374151] leading-relaxed mb-4">
            <strong>Post-inflammatory hyperpigmentation (PIH)</strong>—those dark marks left behind after a pimple heals—is a common concern, especially for those with melanin-rich skin. Being gentle with your skin and avoiding picking can help minimize these marks.
          </p>
          <p className="text-[#374151] leading-relaxed">
            The key is to keep your routine simple and gentle. Over-treating can damage your skin barrier, cause irritation, and—particularly for darker skin tones—lead to more hyperpigmentation.
          </p>
        </div>
      </section>

      {/* Products */}
      <section data-testid="acne-products" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Recommended Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            How to Choose
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start simple:</strong> Cleanser, moisturizer, sunscreen. Add treatments gradually.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Look for "non-comedogenic":</strong> Products labeled this way are less likely to clog pores.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Avoid heavy fragrances:</strong> Fragrance can irritate acne-prone skin.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Prevent PIH:</strong> Don't pick at pimples—this causes more inflammation and darker marks, especially on melanin-rich skin.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Be patient:</strong> Most acne products take 6-12 weeks to show full results.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>See a dermatologist:</strong> For persistent or severe acne, professional help is best.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>
    </Layout>
  );
};

export default Acne;
